import React, { useState, useEffect, useRef } from 'react';
import { Course, Study, UserProfile, Organization, VisibilityLevel, MediaItem } from '../types';
import { saveCourse, updateCourse, deleteCourse, getOrgStudies, uploadImageToStorage } from '../services/firebase';
import { generateBibleImage } from '../services/geminiService';
import { checkAIQuota, incrementAIQuota } from '../services/aiQuotaService';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { MediaPicker } from './MediaPicker';

interface CourseEditorProps {
  user: UserProfile;
  organization: Organization | null;
  existingCourse?: Course;
  onClose: () => void;
  onSave: () => void;
  onLogout: () => void;
}

export const CourseEditor: React.FC<CourseEditorProps> = ({
  user,
  organization,
  existingCourse,
  onClose,
  onSave,
  onLogout,
}) => {
  const [activeTab, setActiveTab] = useState('courses');

  // Course ID - use existing ID or generate a new one for this editing session
  const [courseId] = useState(existingCourse?.id || `course_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const [title, setTitle] = useState(existingCourse?.title || '');
  const [description, setDescription] = useState(existingCourse?.description || '');
  const [tags, setTags] = useState<string[]>(existingCourse?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [studyIds, setStudyIds] = useState<string[]>(existingCourse?.studyIds || []);
  const [visibility, setVisibility] = useState<VisibilityLevel>(existingCourse?.visibility || VisibilityLevel.ORG_ONLY);
  const [requireSequentialViewing, setRequireSequentialViewing] = useState<boolean>(existingCourse?.requireSequentialViewing ?? false);
  const [coverImage, setCoverImage] = useState<string | undefined>(existingCourse?.coverImage);
  const [coverImagePosition, setCoverImagePosition] = useState(existingCourse?.coverImagePosition || { x: 50, y: 50 });
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [availableStudies, setAvailableStudies] = useState<Study[]>([]);
  const [draggedStudyIndex, setDraggedStudyIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [studySearchQuery, setStudySearchQuery] = useState('');
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const imagePreviewRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadAvailableStudies();
  }, [user.orgId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowStudyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAvailableStudies = async () => {
    if (!user.orgId) return;
    const studies = await getOrgStudies(user.orgId);
    setAvailableStudies(studies);
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleGenerateImage = async () => {
    const prompt = imagePrompt.trim() || title;
    if (!prompt) {
      alert('Please enter a title or image prompt first');
      return;
    }

    if (!user.orgId) {
      alert('You must belong to an organization');
      return;
    }

    // Check AI quota before generating
    try {
      await checkAIQuota(user.orgId, 'gemini-image');
    } catch (error: any) {
      alert(error.message || 'AI quota exceeded');
      return;
    }

    setIsGeneratingImage(true);
    try {
      const augmentedPrompt = `${prompt} - scenic background, no text or writing`;
      const imageDataUri = await generateBibleImage(augmentedPrompt);

      const timestamp = Date.now();
      const storagePath = `orgs/${user.orgId}/courses/${courseId}/covers/${timestamp}.png`;
      const downloadURL = await uploadImageToStorage(imageDataUri, storagePath);

      setCoverImage(downloadURL);
      setCoverImagePosition({ x: 50, y: 50 });

      // Increment quota and save to media library
      await incrementAIQuota(user.orgId, user.uid, 'gemini-image', 'Course cover image', downloadURL, undefined, undefined, {
        storagePath,
        prompt: augmentedPrompt,
        sourceType: 'cover',
        associatedCourseId: courseId
      });
    } catch (error) {
      console.error('Failed to generate image:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user.orgId) {
      alert('You must belong to an organization');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64Data = event.target?.result as string;

        const timestamp = Date.now();
        const storagePath = `orgs/${user.orgId}/courses/${courseId}/covers/${timestamp}.png`;
        const downloadURL = await uploadImageToStorage(base64Data, storagePath);

        setCoverImage(downloadURL);
        setCoverImagePosition({ x: 50, y: 50 });
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to upload image. Please try again.');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingImage(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (!isDraggingImage) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!imagePreviewRef.current) return;

      const rect = imagePreviewRef.current.getBoundingClientRect();
      const deltaX = e.clientX - dragStartPos.x;
      const deltaY = e.clientY - dragStartPos.y;

      const deltaXPercent = (deltaX / rect.width) * 100;
      const deltaYPercent = (deltaY / rect.height) * 100;

      const newX = Math.max(0, Math.min(100, coverImagePosition.x + deltaXPercent));
      const newY = Math.max(0, Math.min(100, coverImagePosition.y + deltaYPercent));

      setCoverImagePosition({ x: newX, y: newY });
      setDragStartPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDraggingImage(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingImage, dragStartPos, coverImagePosition]);

  const filteredStudies = availableStudies.filter(study => {
    if (!studySearchQuery.trim()) return true;
    const query = studySearchQuery.toLowerCase();
    return (
      study.title.toLowerCase().includes(query) ||
      study.description.toLowerCase().includes(query) ||
      study.tags.some(tag => tag.toLowerCase().includes(query))
    );
  }).filter(study => !studyIds.includes(study.id));

  const handleAddStudy = (studyId: string) => {
    if (!studyIds.includes(studyId)) {
      setStudyIds([...studyIds, studyId]);
      setStudySearchQuery('');
      setShowStudyDropdown(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedStudyIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedStudyIndex === null || draggedStudyIndex === index) return;

    const newStudyIds = [...studyIds];
    const draggedItem = newStudyIds[draggedStudyIndex];
    newStudyIds.splice(draggedStudyIndex, 1);
    newStudyIds.splice(index, 0, draggedItem);

    setStudyIds(newStudyIds);
    setDraggedStudyIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedStudyIndex(null);
  };

  const moveStudyUp = (index: number) => {
    if (index === 0) return;
    const newStudyIds = [...studyIds];
    [newStudyIds[index - 1], newStudyIds[index]] = [newStudyIds[index], newStudyIds[index - 1]];
    setStudyIds(newStudyIds);
  };

  const moveStudyDown = (index: number) => {
    if (index === studyIds.length - 1) return;
    const newStudyIds = [...studyIds];
    [newStudyIds[index], newStudyIds[index + 1]] = [newStudyIds[index + 1], newStudyIds[index]];
    setStudyIds(newStudyIds);
  };

  const removeStudy = (index: number) => {
    const newStudyIds = [...studyIds];
    newStudyIds.splice(index, 1);
    setStudyIds(newStudyIds);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a course title');
      return;
    }

    if (studyIds.length === 0) {
      alert('Please add at least one study to the course');
      return;
    }

    if (!user.orgId) {
      alert('You must belong to an organization');
      return;
    }

    setIsSaving(true);
    try {
      const courseData: any = {
        orgId: user.orgId,
        authorId: user.uid,
        authorName: user.displayName || 'Unknown',
        title: title.trim(),
        description: description.trim(),
        studyIds,
        visibility,
        tags,
        requireSequentialViewing,
        createdAt: existingCourse?.createdAt || Date.now()
      };

      // Only include optional fields if they have values
      if (coverImage) {
        courseData.coverImage = coverImage;
        courseData.coverImagePosition = coverImagePosition;
      }

      if (existingCourse) {
        await updateCourse(existingCourse.id, courseData);
      } else {
        await saveCourse(courseData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save course:', error);
      alert('Failed to save course. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingCourse) return;

    setIsSaving(true);
    try {
      await deleteCourse(existingCourse.id);
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to delete course:', error);
      alert('Failed to delete course. Please try again.');
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStudyTitle = (studyId: string) => {
    const study = availableStudies.find(s => s.id === studyId);
    return study?.title || 'Unknown Study';
  };

  return (
    <div className="bg-white min-h-screen relative">


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl transform scale-100 animate-bounce-in">
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Delete Course?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this course? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Editor Action Bar */}
      <div className="sticky top-[73px] z-10 bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
        <h2 className="text-xl font-serif font-bold text-gray-800">
          {existingCourse ? 'Edit Course' : 'Create Course'}
        </h2>
        <div className="flex gap-2">
          {existingCourse && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-100"
              title="Delete Course"
            >
              <Icons.Trash className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 transition-colors disabled:opacity-50"
          >
            {isSaving ? <Icons.Loader className="animate-spin w-4 h-4" /> : <Icons.Save className="w-4 h-4" />}
            Save Course
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 pb-24">
        {/* Basic Info Section */}
        <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter course title..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter course description..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as VisibilityLevel)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value={VisibilityLevel.PRIVATE}>Private - Only you can see this course</option>
              <option value={VisibilityLevel.ORG_ONLY}>Organization - Members of {organization?.name || 'your org'} can see this course</option>
              <option value={VisibilityLevel.ALL_ORGS}>All Organizations - Other orgs can copy this course</option>
              <option value={VisibilityLevel.PUBLIC}>Public - Anyone on the internet can view this course</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requireSequentialViewing}
                onChange={(e) => setRequireSequentialViewing(e.target.checked)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Require Sequential Viewing</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              When enabled, users must complete studies in order. When disabled, users can view studies in any order.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-blue-900">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag and press Enter..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button onClick={handleAddTag} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900">
                Add
              </button>
            </div>
          </div>
        </section>

        {/* Cover Image Section */}
        <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cover Image</h3>

          {coverImage && (
            <div className="mb-4">
              <div
                ref={imagePreviewRef}
                className="w-full h-48 bg-cover bg-center rounded-lg relative border-2 border-gray-300"
                style={{
                  backgroundImage: `url(${coverImage})`,
                  backgroundPosition: `${coverImagePosition.x}% ${coverImagePosition.y}%`,
                  cursor: isDraggingImage ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleImageDragStart}
              >
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                  <p className="text-white text-sm font-medium">Click and drag to reposition</p>
                </div>
              </div>
              <button onClick={() => setCoverImage(undefined)} className="mt-2 text-sm text-red-600 hover:text-red-700">
                Remove Image
              </button>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Image Prompt (optional)
              </label>
              <input
                type="text"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="Leave blank to use course title..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button
              onClick={handleGenerateImage}
              disabled={isGeneratingImage}
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGeneratingImage ? (
                <>
                  <Icons.Loader className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Icons.Image className="w-4 h-4" />
                  Generate AI Image
                </>
              )}
            </button>

            <div className="text-center text-sm text-gray-500">OR</div>

            <button
              onClick={() => setShowMediaPicker(true)}
              className="w-full py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200 flex items-center justify-center gap-2"
            >
              <Icons.Image className="w-4 h-4" />
              Select from Library
            </button>

            <div className="text-center text-sm text-gray-500">OR</div>

            <label className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer flex items-center justify-center gap-2">
              <Icons.Upload className="w-4 h-4" />
              Upload Image
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </section>

        {/* Study Selection Section */}
        <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Add Studies</h3>
          <p className="text-sm text-gray-600 mb-4 flex items-start gap-1">
            <Icons.Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Note: Only studies that are not private can be added to a course</span>
          </p>

          {availableStudies.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No studies available. Create some studies first!</p>
          ) : (
            <div className="relative">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={studySearchQuery}
                  onChange={(e) => {
                    setStudySearchQuery(e.target.value);
                    setShowStudyDropdown(true);
                  }}
                  onFocus={() => setShowStudyDropdown(true)}
                  placeholder="Search for studies to add..."
                  className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <Icons.Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>

              {/* Dropdown */}
              {showStudyDropdown && filteredStudies.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                >
                  {filteredStudies.map(study => (
                    <button
                      key={study.id}
                      onClick={() => handleAddStudy(study.id)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{study.title}</div>
                      <div className="text-sm text-gray-500 mt-1">{study.segments.length} segments</div>
                    </button>
                  ))}
                </div>
              )}

              {showStudyDropdown && studySearchQuery && filteredStudies.length === 0 && (
                <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
                  No studies found matching "{studySearchQuery}"
                </div>
              )}
            </div>
          )}
        </section>

        {studyIds.length > 0 && (
          <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Study Order</h3>
              <p className="text-sm text-gray-500 mt-1">Drag to reorder. Users must complete studies in this order.</p>
            </div>

            <div className="space-y-2">
              {studyIds.map((studyId, index) => (
                <div
                  key={studyId}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 bg-white border-2 rounded-lg ${
                    draggedStudyIndex === index ? 'border-primary opacity-50' : 'border-gray-200'
                  } hover:border-primary transition-colors cursor-move`}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-primary text-white rounded-full font-semibold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="flex-1 font-medium text-gray-900">{getStudyTitle(studyId)}</p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveStudyUp(index)}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <Icons.MoveUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveStudyDown(index)}
                      disabled={index === studyIds.length - 1}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <Icons.MoveDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removeStudy(index)}
                      className="p-1 hover:bg-red-50 text-red-600 rounded"
                      title="Remove"
                    >
                      <Icons.X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Media Picker Modal */}
        {showMediaPicker && (
          <MediaPicker
            user={user}
            organization={organization}
            allowedTypes={['image']}
            title="Select Cover Image"
            onSelect={(media: MediaItem) => {
              setCoverImage(media.url);
              setCoverImagePosition({ x: 50, y: 50 });
              setShowMediaPicker(false);
            }}
            onClose={() => setShowMediaPicker(false)}
          />
        )}
      </div>
    </div>
  );
};